import { lazy, Suspense, type ComponentType, type ReactElement } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { PanelLayout } from '@/components/layout/PanelLayout';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { RootLayout } from '@/components/layout/RootLayout';
import {
  RedirectIfAuthenticated,
  RequireAdmin,
  RequireAuth,
} from '@/components/auth/ProtectedRoute';
import { PageSpinner } from '@/components/ui';
import { HomePage } from '@/pages/public/HomePage';

/**
 * Uygulama rotaları. Türkçe yollar SEO için bilinçli tercih (docs/05).
 *
 * **Kod bölme (docs/08 §3):** ana sayfa dışındaki her rota `React.lazy` ile ayrı parçaya
 * çıkar. İlk açılışta yalnız vitrin kabuğu + ana sayfa iner; panel ve yönetim ekranları
 * (uygulamanın büyük kısmı) yalnız o rotaya girildiğinde yüklenir.
 */

/** `lazy()` + `Suspense` sarmalayıcısı — her rotada tekrar yazmamak için. */
function route(loader: () => Promise<Record<string, unknown>>, name: string): ReactElement {
  const Component = lazy(async () => {
    const module = await loader();
    return { default: module[name] as ComponentType };
  });

  return (
    <Suspense fallback={<PageSpinner />}>
      <Component />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  {
    // Kök: kaydırma yönetimi (RootLayout). Asıl rotalar aşağıda, değişmeden.
    element: <RootLayout />,
    children: [
      // Vitrin
      {
        element: <PublicLayout />,
        children: [
          // Ana sayfa lazy DEĞİL: ilk açılışta zaten gerekiyor, ekstra istek anlamsız.
          { path: '/', element: <HomePage /> },
          {
            path: '/ilanlar',
            element: route(() => import('@/pages/public/ListingsPage'), 'ListingsPage'),
          },
          {
            path: '/kategori/:slug',
            element: route(() => import('@/pages/public/ListingsPage'), 'ListingsPage'),
          },
          {
            path: '/ilan/:slug',
            element: route(() => import('@/pages/public/ListingDetailPage'), 'ListingDetailPage'),
          },
          {
            path: '/firma/:slug',
            element: route(
              () => import('@/pages/public/CompanyProfilePage'),
              'PublicCompanyProfilePage',
            ),
          },
          {
            path: '/sayfa/:slug',
            element: route(() => import('@/pages/public/PageDetailPage'), 'PageDetailPage'),
          },
          { path: '/blog', element: route(() => import('@/pages/public/BlogPage'), 'BlogPage') },
          {
            path: '/blog/:slug',
            element: route(() => import('@/pages/public/BlogPostPage'), 'BlogPostPage'),
          },
          {
            path: '/iletisim',
            element: route(() => import('@/pages/public/ContactPage'), 'ContactPage'),
          },
        ],
      },

      // Kimlik doğrulama — girişliyken panele yönlendirilir
      {
        element: <RedirectIfAuthenticated />,
        children: [
          { path: '/giris', element: route(() => import('@/pages/auth/LoginPage'), 'LoginPage') },
          {
            path: '/uye-ol',
            element: route(() => import('@/pages/auth/RegisterPage'), 'RegisterPage'),
          },
          {
            path: '/sifremi-unuttum',
            element: route(() => import('@/pages/auth/ForgotPasswordPage'), 'ForgotPasswordPage'),
          },
          {
            path: '/sifre-sifirla',
            element: route(() => import('@/pages/auth/ResetPasswordPage'), 'ResetPasswordPage'),
          },
        ],
      },

      // Oturumdan bağımsız auth sayfaları
      {
        path: '/kayit-tamamlandi',
        element: route(() => import('@/pages/auth/RegisterSuccessPage'), 'RegisterSuccessPage'),
      },
      {
        path: '/eposta-dogrula',
        element: route(() => import('@/pages/auth/VerifyEmailPage'), 'VerifyEmailPage'),
      },

      // Panel — giriş zorunlu
      {
        element: <RequireAuth />,
        children: [
          {
            element: <PanelLayout />,
            children: [
              {
                path: '/panel',
                element: route(() => import('@/pages/panel/DashboardPage'), 'DashboardPage'),
              },
              {
                path: '/panel/ilanlar',
                element: route(() => import('@/pages/panel/MyListingsPage'), 'MyListingsPage'),
              },
              {
                path: '/panel/ilan-ver',
                element: route(() => import('@/pages/panel/ListingFormPage'), 'ListingFormPage'),
              },
              {
                path: '/panel/ilanlar/:id',
                element: route(() => import('@/pages/panel/ListingFormPage'), 'ListingFormPage'),
              },
              {
                path: '/panel/numuneler',
                element: route(() => import('@/pages/panel/SamplesPage'), 'SamplesPage'),
              },
              {
                path: '/panel/numuneler/:id',
                element: route(() => import('@/pages/panel/SampleDetailPage'), 'SampleDetailPage'),
              },
              {
                path: '/panel/teklifler',
                element: route(() => import('@/pages/panel/OffersPage'), 'OffersPage'),
              },
              {
                path: '/panel/teklifler/:id',
                element: route(() => import('@/pages/panel/OfferDetailPage'), 'OfferDetailPage'),
              },
              {
                path: '/panel/mesajlar',
                element: route(() => import('@/pages/panel/MessagesPage'), 'MessagesPage'),
              },
              {
                path: '/panel/mesajlar/:id',
                element: route(() => import('@/pages/panel/MessagesPage'), 'MessagesPage'),
              },
              {
                path: '/panel/bildirimler',
                element: route(
                  () => import('@/pages/panel/NotificationsPage'),
                  'NotificationsPage',
                ),
              },
              {
                path: '/panel/siparisler',
                element: route(() => import('@/pages/panel/OrdersPage'), 'OrdersPage'),
              },
              {
                path: '/panel/siparisler/:id',
                element: route(() => import('@/pages/panel/OrderDetailPage'), 'OrderDetailPage'),
              },
              {
                path: '/panel/aramalarim',
                element: route(
                  () => import('@/pages/panel/SavedSearchesPage'),
                  'SavedSearchesPage',
                ),
              },
              {
                path: '/panel/aramalarim/:id',
                element: route(
                  () => import('@/pages/panel/SavedSearchesPage'),
                  'SavedSearchesPage',
                ),
              },
              {
                path: '/panel/tesisler',
                element: route(() => import('@/pages/panel/FacilitiesPage'), 'FacilitiesPage'),
              },
              {
                path: '/panel/firma',
                element: route(
                  () => import('@/pages/panel/CompanyProfilePage'),
                  'CompanyProfilePage',
                ),
              },
              {
                path: '/panel/favoriler',
                element: route(() => import('@/pages/panel/FavoritesPage'), 'FavoritesPage'),
              },
              {
                path: '/panel/ayarlar',
                element: route(() => import('@/pages/panel/SettingsPage'), 'SettingsPage'),
              },
            ],
          },
        ],
      },

      // Yönetim — ADMIN rolü zorunlu (docs/05 §5)
      {
        element: <RequireAuth />,
        children: [
          {
            element: <RequireAdmin />,
            children: [
              {
                element: <AdminLayout />,
                children: [
                  {
                    path: '/admin',
                    element: route(
                      () => import('@/pages/admin/AdminDashboardPage'),
                      'AdminDashboardPage',
                    ),
                  },
                  {
                    path: '/admin/firmalar',
                    element: route(
                      () => import('@/pages/admin/AdminCompaniesPage'),
                      'AdminCompaniesPage',
                    ),
                  },
                  {
                    path: '/admin/ilanlar',
                    element: route(
                      () => import('@/pages/admin/AdminListingsPage'),
                      'AdminListingsPage',
                    ),
                  },
                  {
                    path: '/admin/kullanicilar',
                    element: route(() => import('@/pages/admin/AdminUsersPage'), 'AdminUsersPage'),
                  },
                  {
                    path: '/admin/siparisler',
                    element: route(
                      () => import('@/pages/admin/AdminOrdersPage'),
                      'AdminOrdersPage',
                    ),
                  },
                  {
                    path: '/admin/siparisler/:id',
                    element: route(
                      () => import('@/pages/admin/AdminOrderDetailPage'),
                      'AdminOrderDetailPage',
                    ),
                  },
                  {
                    path: '/admin/numuneler',
                    element: route(
                      () => import('@/pages/admin/AdminSamplesPage'),
                      'AdminSamplesPage',
                    ),
                  },
                  {
                    path: '/admin/fiyat-talepleri',
                    element: route(
                      () => import('@/pages/admin/AdminPriceRequestsPage'),
                      'AdminPriceRequestsPage',
                    ),
                  },
                  {
                    path: '/admin/degerlendirmeler',
                    element: route(
                      () => import('@/pages/admin/AdminReviewsPage'),
                      'AdminReviewsPage',
                    ),
                  },
                  {
                    path: '/admin/icerik',
                    element: route(
                      () => import('@/pages/admin/AdminContentPage'),
                      'AdminContentPage',
                    ),
                  },
                  {
                    path: '/admin/kategoriler',
                    element: route(
                      () => import('@/pages/admin/AdminCategoriesPage'),
                      'AdminCategoriesPage',
                    ),
                  },
                  {
                    path: '/admin/ayarlar',
                    element: route(
                      () => import('@/pages/admin/AdminSettingsPage'),
                      'AdminSettingsPage',
                    ),
                  },
                  {
                    path: '/admin/denetim',
                    element: route(() => import('@/pages/admin/AdminAuditPage'), 'AdminAuditPage'),
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        element: <PublicLayout />,
        children: [
          { path: '*', element: route(() => import('@/pages/NotFoundPage'), 'NotFoundPage') },
        ],
      },
    ],
  },
]);
